import React, { useState, useRef } from 'react';
import { UploadCloud, CheckCircle, Copy, CreditCard, Smartphone, Info, Building2, ArrowRight } from 'lucide-react';
import ApiService from '../services/apiService';

export const PaymentCheckout = ({ userId }) => {
    const [file, setFile] = useState(null);
    const [statusMsg, setStatusMsg] = useState("");
    const [isDragging, setIsDragging] = useState(false);
    const [copied, setCopied] = useState(false);
    const [copiedBank, setCopiedBank] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState('vodafone'); // 'vodafone' | 'bank'

    const fileInputRef = useRef(null);

    const phoneNumber = "+20 111 611 4118";

    const handleCopy = () => {
        navigator.clipboard.writeText(phoneNumber);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = () => {
        setIsDragging(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            setFile(e.dataTransfer.files[0]);
        }
    };

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            setFile(e.target.files[0]);
        }
    };

    const handleUpload = async () => {
        if (!file) {
            setStatusMsg("Please select or drop a screenshot first.");
            return;
        }

        setIsSubmitting(true);
        const formData = new FormData();
        formData.append("file", file);

        try {
            const method = paymentMethod === 'bank' ? 'BankTransfer' : 'VodafoneCash';
            await ApiService.post(`/admin/payments/upload?user_id=${userId}&amount=100&method=${method}`, formData, true);
            setStatusMsg("Receipt uploaded! Awaiting Admin approval.");
            setFile(null);
        } catch (e) {
            setStatusMsg("Failed to upload. Connection error.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCopyBank = (text, key) => {
        navigator.clipboard.writeText(text);
        setCopiedBank(key);
        setTimeout(() => setCopiedBank(''), 2000);
    };

    return (
        <div className="max-w-3xl mx-auto p-4 md:p-8 animate-fade-in">
            <div className="text-center mb-10">
                <h2 className="text-4xl font-extrabold text-[#1B263B] dark:text-white mb-3 tracking-tight">Top Up AI Credits</h2>
                <p className="text-[#6B8F71] dark:text-gray-400 font-medium">Unlock infinite agricultural insights across your farms.</p>
            </div>

            <div className="grid grid-cols-1 gap-8">
                {/* Payment Method Tabs */}
                <div className="flex justify-center">
                    <div className="flex bg-white/90 dark:bg-slate-900/90 backdrop-blur-md rounded-full p-1 shadow-lg border border-gray-200 dark:border-slate-700">
                        <button onClick={() => setPaymentMethod('vodafone')} className={`px-6 py-2.5 rounded-full font-bold text-sm transition-all flex items-center gap-2 ${paymentMethod === 'vodafone' ? 'bg-[#1B263B] dark:bg-slate-700 text-white shadow-md' : 'text-gray-500 dark:text-gray-400 hover:text-[#1B263B] dark:hover:text-white'}`}>
                            <Smartphone size={16} /> Vodafone Cash / InstaPay
                        </button>
                        <button onClick={() => setPaymentMethod('bank')} className={`px-6 py-2.5 rounded-full font-bold text-sm transition-all flex items-center gap-2 ${paymentMethod === 'bank' ? 'bg-[#1B263B] dark:bg-slate-700 text-white shadow-md' : 'text-gray-500 dark:text-gray-400 hover:text-[#1B263B] dark:hover:text-white'}`}>
                            <Building2 size={16} /> Bank Transfer
                        </button>
                    </div>
                </div>
                {/* ═══ VODAFONE CASH / INSTAPAY ═══ */}
                {paymentMethod === 'vodafone' && (
                <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-3xl p-8 shadow-xl relative overflow-hidden transition-colors animate-fade-in">
                    <div className="absolute top-0 right-0 p-4 opacity-5 dark:opacity-10 text-[#C47A2C]">
                        <Smartphone size={120} />
                    </div>
                    <h3 className="text-xl font-bold text-[#1B263B] dark:text-white mb-4 flex items-center gap-3">
                        <span className="bg-[#6B8F71]/20 text-[#6B8F71] w-8 h-8 rounded-full flex items-center justify-center font-bold shadow-sm">1</span>
                        Transfer Funds
                    </h3>

                    <p className="text-gray-600 dark:text-slate-300 text-sm mb-6 leading-relaxed relative z-10">
                        Please transfer the desired subscription amount securely via <strong>Vodafone Cash</strong> or <strong>InstaPay</strong> to the dedicated merchant number below.
                    </p>

                    <div className="w-full bg-[#F4EFEA]/50 dark:bg-slate-950 border border-[#1B263B]/10 dark:border-slate-800 rounded-2xl p-6 sm:p-8 flex flex-col items-center justify-center mb-6 relative z-10 shadow-sm">
                        <p className="text-[10px] sm:text-xs text-gray-500 dark:text-slate-500 uppercase tracking-widest mb-3 font-bold text-center">Official Merchant Number</p>
                        <div className="flex items-center justify-center gap-3 sm:gap-4 w-full">
                            <span className="text-xl sm:text-2xl md:text-3xl font-mono text-[#C47A2C] font-black tracking-tight whitespace-nowrap">{phoneNumber}</span>
                            <button
                                onClick={handleCopy}
                                className={`p-2.5 sm:p-3 rounded-xl flex-shrink-0 transition-all ${copied ? 'bg-[#6B8F71] text-white' : 'bg-white dark:bg-slate-800 text-gray-400 dark:text-slate-400 border border-gray-200 dark:border-slate-700 hover:text-[#C47A2C] dark:hover:text-white shadow-md'}`}
                                title="Copy Number"
                            >
                                {copied ? <CheckCircle size={22} /> : <Copy size={22} />}
                            </button>
                        </div>
                        {copied && <span className="text-sm text-[#6B8F71] mt-3 font-bold animate-fade-in">Copied to clipboard!</span>}
                    </div>

                    <div className="flex items-start gap-3 p-4 bg-[#C47A2C]/10 border border-[#C47A2C]/20 rounded-xl text-[#A36322] dark:text-[#C47A2C] text-xs shadow-inner relative z-10">
                        <Info size={18} className="shrink-0 mt-0.5" />
                        <p className="font-medium leading-relaxed">Take a screenshot of the successful transaction page. You will need it for verification in the next step.</p>
                    </div>
                </div>
                )}

                {/* ═══ BANK TRANSFER ═══ */}
                {paymentMethod === 'bank' && (
                <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-3xl p-8 shadow-xl relative overflow-hidden transition-colors animate-fade-in">
                    <div className="absolute top-0 right-0 p-4 opacity-5 dark:opacity-10 text-[#C47A2C]">
                        <Building2 size={120} />
                    </div>
                    <h3 className="text-xl font-bold text-[#1B263B] dark:text-white mb-4 flex items-center gap-3">
                        <span className="bg-[#6B8F71]/20 text-[#6B8F71] w-8 h-8 rounded-full flex items-center justify-center font-bold shadow-sm">1</span>
                        Bank Transfer Details
                    </h3>

                    <p className="text-gray-600 dark:text-slate-300 text-sm mb-6 leading-relaxed relative z-10">
                        Transfer the subscription amount to the following verified bank account. Include your registered email as the transfer reference.
                    </p>

                    <div className="space-y-4 relative z-10 mb-6">
                        {[
                            { label: 'Bank Name', value: 'National Bank of Egypt (NBE)', key: 'bank' },
                            { label: 'Account Number', value: '1234567890123456', key: 'account' },
                            { label: 'Recipient Name', value: 'GeoNutria Agricultural Solutions', key: 'recipient' },
                            { label: 'IBAN', value: 'EG12 0003 0004 0000 1234 5678 901', key: 'iban' },
                        ].map(item => (
                            <div key={item.key} className="flex items-center justify-between bg-[#F4EFEA]/50 dark:bg-slate-950 border border-[#1B263B]/10 dark:border-slate-800 rounded-xl px-5 py-4">
                                <div>
                                    <p className="text-[10px] text-gray-500 dark:text-slate-500 uppercase tracking-widest font-bold">{item.label}</p>
                                    <p className="text-sm font-bold text-[#1B263B] dark:text-white mt-1 font-mono" dir="ltr">{item.value}</p>
                                </div>
                                <button
                                    onClick={() => handleCopyBank(item.value, item.key)}
                                    className={`p-2 rounded-lg flex-shrink-0 transition-all ${copiedBank === item.key ? 'bg-[#6B8F71] text-white' : 'bg-white dark:bg-slate-800 text-gray-400 dark:text-slate-400 border border-gray-200 dark:border-slate-700 hover:text-[#C47A2C] shadow-sm'}`}
                                    title={`Copy ${item.label}`}
                                >
                                    {copiedBank === item.key ? <CheckCircle size={16} /> : <Copy size={16} />}
                                </button>
                            </div>
                        ))}
                    </div>

                    <div className="flex items-start gap-3 p-4 bg-[#C47A2C]/10 border border-[#C47A2C]/20 rounded-xl text-[#A36322] dark:text-[#C47A2C] text-xs shadow-inner relative z-10">
                        <Info size={18} className="shrink-0 mt-0.5" />
                        <div className="font-medium leading-relaxed">
                            <p className="mb-1"><strong>Important:</strong> Include your registered email address in the transfer reference/notes field.</p>
                            <p>After completing the transfer, take a screenshot of the confirmation page and upload it below.</p>
                        </div>
                    </div>
                </div>
                )}

                {/* Step 2: Upload Screenshot */}
                <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-3xl p-8 shadow-xl flex flex-col justify-between transition-colors">
                    <div>
                        <h3 className="text-xl font-bold text-[#1B263B] dark:text-white mb-4 flex items-center gap-3">
                            <span className="bg-[#6B8F71]/20 text-[#6B8F71] w-8 h-8 rounded-full flex items-center justify-center font-bold shadow-sm">2</span>
                            Verify Transaction
                        </h3>
                        <p className="text-gray-600 dark:text-slate-300 text-sm mb-6">
                            Upload your payment receipt screenshot here to instantly credit your account.
                        </p>

                        <div
                            className={`border-2 border-dashed rounded-xl p-8 flex flex-col items-center justify-center text-center cursor-pointer transition-all duration-300 min-h-[220px]
                                ${isDragging ? 'border-[#C47A2C] bg-[#C47A2C]/5 scale-[1.02]' : 'border-gray-200 dark:border-slate-700 bg-gray-50/50 dark:bg-slate-950/50 hover:border-[#C47A2C]/50'}
                                ${file ? 'border-[#6B8F71] bg-[#6B8F71]/5' : ''}
                            `}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <input
                                type="file"
                                className="hidden"
                                ref={fileInputRef}
                                onChange={handleFileChange}
                                accept="image/*"
                            />

                            {file ? (
                                <div className="animate-fade-in flex flex-col items-center">
                                    <div className="w-16 h-16 bg-[#6B8F71]/20 rounded-full flex items-center justify-center mb-3 shadow-inner">
                                        <CheckCircle size={32} className="text-[#6B8F71]" />
                                    </div>
                                    <p className="text-[#1B263B] dark:text-white font-bold mb-1 truncate max-w-[200px]">{file.name}</p>
                                    <p className="text-gray-400 dark:text-slate-500 text-xs font-medium">{(file.size / 1024 / 1024).toFixed(2)} MB • Click to replace</p>
                                </div>
                            ) : (
                                <div className="flex flex-col items-center text-gray-400 dark:text-slate-400">
                                    <UploadCloud size={56} className="mb-4 text-[#1B263B]/20 dark:text-slate-600" />
                                    <p className="font-bold text-[#1B263B] dark:text-slate-300 mb-1">Click or drag receipt here</p>
                                    <p className="text-xs">Supports JPG, PNG, WEBP</p>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="mt-8">
                        <button
                            className={`w-full py-4 rounded-xl font-bold flex items-center justify-center gap-2 transition-all shadow-lg text-lg
                                ${!file || isSubmitting ? 'bg-gray-100 dark:bg-slate-800 text-gray-400 dark:text-slate-500 cursor-not-allowed shadow-none' : 'bg-[#C47A2C] hover:bg-[#A36322] text-white transform hover:-translate-y-0.5'}
                            `}
                            onClick={handleUpload}
                            disabled={!file || isSubmitting}
                        >
                            {isSubmitting ? (
                                <>
                                    <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                                    Uploading...
                                </>
                            ) : (
                                <>
                                    <CreditCard size={22} />
                                    Submit for Verification
                                </>
                            )}
                        </button>

                        {statusMsg && (
                            <div className={`mt-4 p-4 rounded-xl text-sm text-center font-bold animate-fade-in shadow-inner ${statusMsg.includes("Failed") || statusMsg.includes("Please") ? 'bg-rose-50 border border-rose-100 text-rose-600 dark:bg-rose-500/10 dark:border-rose-500/30 dark:text-rose-400' : 'bg-[#6B8F71]/10 border border-[#6B8F71]/20 text-[#527056] dark:text-[#6B8F71]'}`}>
                                {statusMsg}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
